import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Strateji Danışmanlığı — DAIMI, AIMI ve Strateji Danışmanlığı tek akışta.
 *
 * DAIMI'nin Türkçe adı Digitopia'nın kendi kullanımından alındı: digitopia.co'da
 * "Dijital ve Yapay Zeka Olgunluk Endeksi" olarak geçiyor. Yeni bir karşılık
 * uydurmak, sekiz yıldır kullanılan bir ismi ikiye bölmek olurdu.
 */
/** Impact Platform — hero'daki "Hemen başlayın" buradan açılıyor. */
const PLATFORM = 'https://impact.digitopia.ai'

export const strategyConsulting: WrittenPage = {
  navLabel: 'Strateji Danışmanlığı',
  title: 'Strateji Danışmanlığı: DAIMI ve AIMI',
  description:
    'Savunulabilir bir başlangıç noktası kurun, değer üreteni önceliklendirin, olgunluk artışını P&L etkisine çevirin. DAIMI, AIMI ve Strateji Danışmanlığı.',
  sections: [
    {
      kind: 'hero',
      headline:'Öncelik kavgasını bitirin.',
      sub: 'Ajan tabanlı yapay zeka yolculuğunuzu kurgulayın ve önceliklendirin. Olgunluk değerlendirmesinde ve olgunluk artışını iş değerine çevirmede dünya lideriyiz; 40+ ülkede 3.000+ değerlendirme yaptık.',
      photo: {
        src: '/photo/boardroom-daimi-tablet.jpg',
        alt: 'Elinde tablet tutan bir Digitopia danışmanı, kıyaslamaya göre puanlanmış altı boyuttan oluşan DAIMI karnesini üç yöneticiye anlatıyor.',
      },
      ctas: [
        { label: 'Bize Ulaşın', href: '#contact' },
        { label: 'Hemen başlayın', href: PLATFORM },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      size: 'lg',
      tightTop: true,
      paragraphs: [
        'On iki yönetici, on iki öncelik listesiyle geliyor; her biri kendi içinde savunulabilir. Yapay zekanın önemli olduğunda herkes anlaşıyor. Neyin ilk geleceğinde hiç kimse. Hepsinin bir iş gerekçesi, bir sahibi ve bekleyemeyeceğine dair bir nedeni var. Ortak bir olgu tabanı yoksa en yüksek ses kazanır. Hak ettiğiniz bu değil.',
        'Bir değerlendirme, yönetim ekibinizin üzerine hareket edebileceği üç soruyu yanıtladığında hakkını verir. Bugün neredeyiz. Nerede olmamız gerekiyor. İlk olarak ne değişmeli.',
      ],
    },
    {
      kind: 'features',
      heading: 'Elinize ne geçer',
      columns: 3,
      items: [
        {
          title: 'Scorecard & Benchmark',
          body: 'Altı boyut; kendi sektörünüz ve küresel set ile kıyaslanmış. Tartışma kanaatten kanıta kayar.',
        },
        {
          title: 'Strategy & Roadmap',
          body: 'Değere ve gerçekten sahip olduğunuz değişim kapasitesine göre sıralanır; düşük değerli iş sessizce ertelenmek yerine durdurulur.',
        },
        {
          title: 'Business Value Correlation',
          body: 'Her olgunluk puanı, hareket ettirdiği KPI ve P&L satırıyla eşleştirilir.',
        },
      ],
    },
    {
      kind: 'scale',
      heading: 'DAIMI. Dijital ve Yapay Zeka Olgunluk Endeksi.',
      sub: 'Kurumun tamamına bakan görünüm.',
      intro:
        'Altı boyut: Müşteri, Operasyon, İnovasyon, İnsan, Yönetişim ve Teknoloji. Başlangıçtan dijitale beş seviye. 40+ ülke ve 20+ sektörden 3.000+ ölçümle kıyaslanır.',
      // Beş seviyelik liste yerine endeksin kendi görseli, gri bandın içinde.
      // Çarkın Türkçe basılmış hâli var, o yüzden bu sayfa artık İngilizce
      // export'u değil onu gösteriyor.
      photo: {
        src: '/photo/daimi-framework-wheel-turkce-labelled.png',
        alt: 'DAIMI çarkı: Dijital ve Yapay Zeka Olgunluk Endeksi’nin çevresinde Müşteri, Operasyon, İnovasyon, İnsan, Yönetişim ve Teknoloji; her birinin altında ölçülen alt boyutlarla.',
      },
      caption: 'Altı DAIMI boyutu ve her birinin altında ne ölçüldüğü.',
      cta: { label: 'DAIMI\'yi keşfedin', href: LINKS.daimi },
    },
    {
      // Hesaplayıcının başlığı — İngilizce sayfadaki bölümün karşılığı.
      kind: 'prose',
      heading: 'DAIMI’nin etkisini kendi rakamlarınızla görün',
      paragraphs: [
        'Dört girdi — cironuz, ülkeniz, sektörünüz ve ne yapmaya çalıştığınız — yukarıdaki olgunluk anlatısını yönetim kurulu masasına götürebileceğiniz bir rakama çeviriyor.',
      ],
    },
    {
      // ROI hesaplayıcı, İngilizce sayfadaki yeriyle aynı yerde: DAIMI bandının
      // hemen altı (Halil, 29.08.2026). Aracın kendi metni henüz Türkçe değil,
      // bu yüzden `lang="en"` sarmalıyla İngilizce görünür — Guts Check ve ARI
      // ile aynı durum, ContentPage'deki `tool` dalı bunu kendisi yapıyor.
      kind: 'tool',
      tool: 'roi-calculator',
    },
    {
      kind: 'photo',
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'Bir Digitopia danışmanı iki yöneticiye 3,4 olan DAIMI puanını 3,7 hedefiyle boyut boyut karşılaştırarak anlatıyor.',
      },
    },
    {
      // Yukarıdaki DAIMI ile aynı şekil: tek bant, anlatı ve onu taşıyan çark.
      kind: 'scale',
      heading: 'AIMI. Yapay Zeka Olgunluk Endeksi.',
      sub: 'Soru daha dar ve daha acil olduğunda.',
      intro:
        'Yapay zeka olgunluğu modellerle ilgili değildir. Veri temeline, iş akışı tasarımına, insan muhakemesine, yönetişime, ölçüm disiplinine ve ölçeklenebilir mimariye bağlıdır. AIMI tam olarak bunları ölçer; böylece organizasyonunuzun zaten duyurduğu yapay zeka iddiasını taşıyıp taşıyamayacağını öğrenirsiniz.',
      pullquote: 'Kaosun etrafına yapay zeka sarmak inovasyon üretmez. Sorumluluk üretir.',
      photo: {
        src: '/photo/aimi-framework-wheel.webp',
        alt: 'AIMI çarkı: Yapay Zeka Olgunluk Endeksi’nin çevresinde Capabilities, Governance, Technology, Customer, Operations ve Administration; her birinin altında ölçülen alt boyutlarla.',
      },
      caption: 'Altı AIMI boyutu ve her birinin altında ne ölçüldüğü.',
      // Yukarıdaki DAIMI bandı "DAIMI'yi keşfedin" ile bitiyor; bu bant da
      // AIMI çözüm sayfasına aynı yerden gitsin.
      cta: { label: 'AIMI\'yi keşfedin', href: LINKS.aimi },
    },
    {
      kind: 'list',
      heading: 'Strateji Danışmanlığı. İş, sayının bittiği yerde başlar.',
      intro:
        'Bir puan tek başına hiçbir şeyi değiştirmez. Danışmanlarımız ardından gelen kararlar için odada kalır: neyin durdurulacağı, neyin sıraya konacağı, hangi sonucun sahibinin kim olduğu ve yönetim kurulunun her çeyrek ne göreceği.',
      items: [
        'Yönetim ekibine sunulan değil, yönetim ekibiyle mutabık kalınan hedef durum tanımı',
        'Belirli KPI\'lara ve P&L satırlarına bağlanmış yatırım gerekçeleri',
        'Operasyon modeli ve yönetişim tercihleri',
        'Yönetici hizalama oturumları, rahatsız edici olanlar dahil',
        'Yıllık yeniden değerlendirme ve çeyreklik iş gözden geçirmeleri',
      ],
      cta: { label: 'Bize Ulaşın', href: '#contact' },
    },
    {
      kind: 'features',
      heading: 'Nasıl çalışıyoruz',
      columns: 2,
      textSize: 'lg',
      items: [
        {
          title: 'İnsan uzmanlığı',
          body: 'Kıdemli danışmanlar, karşılıklı ve sorgulayıcı oturumlar yürütür. Kendi kendine puanlanan anket yok. Bir ekip kendine cömert davrandığında da, haksızlık ettiğinde de müdahale ederiz.',
        },
        {
          title: 'Ajan tabanlı uygulama',
          body: 'Altta çalışan otomasyon motoru Discovery Platform. Daha hızlı, daha iyi, daha erişilebilir; tamamen dijital ve yapay zeka destekli.',
        },
      ],
    },
    {
      // İngilizcesiyle aynı: iki madde işareti yerine, müşterilerin kendi
      // sözleri — Hall of Fame kartının sadeleştirilmiş hâli, şirket başına bir
      // sekme.
      kind: 'quotes',
      variant: 'minimal-carousel',
      // Başlık İngilizcesiyle birlikte "Success Stories" oldu (2026-08-31) ve
      // aynı gün üç sekme daha eklendi: Kordsa, TEMSA, Akçansa.
      heading: 'Başarı Hikâyeleri',
      items: [
        {
          company: 'LC Waikiki',
          logo: { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png' },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
          quote: [
            'Stratejik önceliklendirme 2021’den bu yana bizim için temel bir odak oldu. '
            + 'Perakende, lojistik ve e-ticaret gibi iş birimlerimizin olgunluk seviyeleri '
            + 'birbirinden farklı olduğu için hizalanma kritik. Değerlendirmeyi tam bir '
            + 'şeffaflıkla yapmak, rotada kalmamızı ve bilinçli kararlar vermemizi sağlıyor.',
          ],
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          cta: { label: 'Devamını okuyun', href: '/en/customers/lc-waikiki' },
        },
        {
          company: 'Brisa',
          logo: { name: 'Brisa', src: '/logo/brisacard.png' },
          portrait: { src: '/photo/portrait-filiz-mert.png', alt: 'Filiz Mert' },
          quote: [
            'Dijital dönüşüm çözümünü 3 yıldır düzenli olarak yeniliyoruz. Bu yıl çalışmayı '
            + 'öne çekerek Digital Maturity Index (DMI) sonuçlarını strateji toplantımızda '
            + 'değerlendirmeyi ve şirket stratejilerimizi bu çıktılara göre belirleyip aksiyon '
            + 'planımıza daha fazla yansıtmayı planlıyoruz. Güçlü olduğumuz alanları, '
            + 'geliştirmemiz gereken kasları ve dijital dönüşüm yolculuğumuzda son 3 yılda '
            + 'kat ettiğimiz yolu görmeye devam etmek için DMI’yı yenilemeyi sürdüreceğiz.',
          ],
          source: 'Filiz Mert, Digital Projects & Analytics Manager',
          cta: { label: 'Devamını okuyun', href: '/en/customers/brisa' },
        },
        {
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
          quote: [
            'Digitopia’nın DMI değerlendirmeleri, net ve uygulanabilir dijital yol haritaları '
            + 'oluşturmamıza ve rotada kalmamıza yardımcı oldu. Öngörüleri bizim için çok '
            + 'değerliydi; yapay zeka gibi kritik alanlara, bu konu gündemin merkezine '
            + 'oturmadan çok önce odaklanmamızı sağladı.',
            'Digitopia bizim için yalnızca bir tedarikçi değil. Dijital stratejimizi '
            + 'şekillendirmede ve yürütmede temel bir rol oynuyor.',
          ],
          source: 'Fatih Akar, Director of Global Information Technologies',
          cta: { label: 'Devamını okuyun', href: '/en/customers/kordsa' },
        },
        {
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
          quote: [
            'Nerede durduğumuza ve hem holding içinde hem de sektörde nasıl konumlandığımıza '
            + 'dair net ve nesnel bir görünüm sağladı. Daha da önemlisi, ilerlememizi zaman '
            + 'içinde takip etmemize ve dijital yatırımlarımızı ölçülebilir sonuçlarla '
            + 'hizalamamıza imkân verdi. Bu yapılandırılmış yaklaşım, dönüşüm çalışmalarımıza '
            + 'netlik, odak ve güven kazandırıyor.',
          ],
          source: 'Erdinç Uğurlu, CIO, TEMSA',
          cta: { label: 'Devamını okuyun', href: '/en/customers/temsa' },
        },
        {
          company: 'Akçansa',
          logo: { name: 'Akçansa', src: '/logo/akcansacard.png' },
          portrait: { src: '/photo/portrait-aydin-sener.png', alt: 'Aydın Şener' },
          quote: [
            'Digitopia’nın araçlarını kullanmak bizim için son derece değerli oldu. DMI ve '
            + 'Impact Platform, dijital dönüşüm için net ve yapılandırılmış bir yol haritasına '
            + 'kavuşmamızı sağladı. İK, satış ve BT’den fonksiyonlar arası ekipleri sürece '
            + 'dahil ederek öncelikler üzerinde hizalandık, şirket genelinde farkındalık '
            + 'yarattık ve değişimi yürütecek kilit projeleri belirledik.',
          ],
          source: 'Aydın Şener, Digital Transformation and Industry 4.0 Manager',
          cta: { label: 'Devamını okuyun', href: '/en/customers/akcansa' },
        },
      ],
      cta: { label: 'Başarı hikâyelerini görün', href: LINKS.successStories },
      check:
        'Bu bölüm yeniden tasarlandı: iki kanıt maddesinin yerini müşterilerin kendi sözleri '
        + 'aldı. Böylece üç iddia sayfadan çıktı — LC Waikiki’nin yıllık DAIMI puanını şirket '
        + 'OKR’ı yapması, Brisa’nın dört yılda 3,1’den 3,8’e çıkması ve ISO 42001 sertifikası. '
        + 'Kartların üstünde bir satır olarak geri istenirse söylemeniz yeterli. Ayrıca iki '
        + 'alıntı da Türkçeye çevrildi; ikisi de aslında İngilizce yayımlanmış sözler, '
        + 'yayımlanmadan önce onay ister. Aynısı 2026-08-31’de eklenen Kordsa, TEMSA ve '
        + 'Akçansa alıntıları için de geçerli: üçü de İngilizce verilmiş sözlerin Türkçeye '
        + 'çevirisi.',
    },
    // Kapanıştaki siyah CTA bandı kaldırıldı (2026-08-30) — sayfanın altındaki
    // form zaten orada, İngilizcesiyle aynı gerekçe.
  ],
}
